import type {FC} from 'react';
import {useState} from 'react';
import {AssistantMemoryKindEnum} from 'src/api/ProactApiClient';
import {Button} from 'src/design/atoms/Button';
import {Group} from 'src/design/atoms/Group';
import {Paper} from 'src/design/atoms/Paper';
import {SelectBox} from 'src/design/atoms/SelectBox';
import {Stack} from 'src/design/atoms/Stack';
import {Textbox} from 'src/design/atoms/Textbox';
import {
  MEMORY_KIND_OPTIONS,
  MEMORY_TEXT_MAX_LENGTH,
  memoryKindDescription,
  memoryKindExample,
} from './assistantMemoryFormat';

export interface ScoutMemoryNoteValues {
  kind: AssistantMemoryKindEnum;
  text: string;
}

export interface ScoutMemoryNoteFormProps {
  initialValues?: ScoutMemoryNoteValues;
  saving: boolean;
  onSubmit: (values: ScoutMemoryNoteValues) => void;
  onCancel: () => void;
}

const EMPTY_VALUES: ScoutMemoryNoteValues = {
  kind: AssistantMemoryKindEnum.Preference,
  text: '',
};

/**
 * Inline add/edit form for one Scout memory note: kind, note text with a live count, Cancel/Save.
 * @registry Scout memory note form shown in place of the Add button or a note row.
 */
export const ScoutMemoryNoteForm: FC<ScoutMemoryNoteFormProps> = ({
  initialValues = EMPTY_VALUES,
  saving,
  onSubmit,
  onCancel,
}) => {
  const [values, setValues] = useState(initialValues);
  const trimmedText = values.text.trim();

  return (
    <Paper withBorder p="sm">
      <Stack gap={0}>
        <SelectBox
          label="Kind"
          ariaLabel="Note kind"
          description={memoryKindDescription(values.kind)}
          options={MEMORY_KIND_OPTIONS}
          value={String(values.kind)}
          onSelectChangeCallback={value => {
            if (value)
              setValues(prev => ({
                ...prev,
                kind: Number(value) as AssistantMemoryKindEnum,
              }));
          }}
        />
        <Textbox
          label="Note"
          ariaLabel="Note text"
          placeholder={memoryKindExample(values.kind)}
          value={values.text}
          multiline
          minRows={2}
          maxRows={4}
          maxLength={MEMORY_TEXT_MAX_LENGTH}
          helperText={`${values.text.length}/${MEMORY_TEXT_MAX_LENGTH}`}
          onChangeCallback={event => {
            const text = event.currentTarget.value;
            setValues(prev => ({...prev, text}));
          }}
        />
        <Group gap="sm">
          <Button variant="subtle" color="gray" size="compact-sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            size="compact-sm"
            loading={saving}
            disabled={!trimmedText}
            onClick={() => onSubmit({kind: values.kind, text: trimmedText})}>
            Save
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
};
